import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { usePermissions } from './auth/usePermissions.js';
import { ORG } from './auth/capabilities.js';
import { DeniedState } from './auth/DeniedState.jsx';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { PageHead, StatCard, ProgressBar, ScoreRing } from '../platform/_ui';
import * as api from '../api/org-health.api.js';

const SCORE_LEVEL = (s) => {
  if (s >= 90) return { label: 'Excellent', tone: 'green',  color: 'var(--nx-success)' };
  if (s >= 70) return { label: 'Good',      tone: 'green',  color: 'var(--nx-success)' };
  if (s >= 50) return { label: 'Fair',      tone: 'amber',  color: 'var(--nx-warning)' };
  return             { label: 'Needs Attention', tone: 'red', color: 'var(--nx-danger)' };
};

const REC_ICON  = { security: 'Shield', license: 'Key', storage: 'HardDrive', growth: 'TrendingUp' };
const REC_TONE  = { security: 'red',    license: 'amber', storage: 'blue',     growth: 'green'      };

const ScoreBar = ({ label, score, icon }) => {
  const level = SCORE_LEVEL(score);
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 500, color: 'var(--nx-text)' }}>
          <NexusIcon name={icon} size={14} style={{ color: 'var(--nx-text-muted)' }} />{label}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: level.color }}>{score}%</span>
          <Pill tone={level.tone}>{level.label}</Pill>
        </span>
      </div>
      <ProgressBar value={score} max={100} color={level.color} height={7} label={label} />
    </div>
  );
};

export default function OrgHealth({ data }) {
  const { orgId }  = useParams();
  const navigate   = useNavigate();
  const [searchParams] = useSearchParams();
  const toast      = useToast();
  const perm       = usePermissions();

  const returnTo    = searchParams.get('returnTo');
  const returnLabel = searchParams.get('returnLabel');
  const [health, setHealth]   = useState(null);
  const [loading, setLoading] = useState(true);

  const org = data.state.organizations.find(o => o.id === orgId);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const h = await api.getHealth(orgId);
      setHealth(h);
    } catch { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to load health data' }); }
    finally { setLoading(false); }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  if (!org) return null;

  const overallScore = health?.overallScore ?? health?.score ?? 0;
  const level        = SCORE_LEVEL(overallScore);

  if (!org) return null;

  if (!perm.has(ORG.HEALTH_VIEW) && !perm.isSA) return (
    <div className="nx-cc-page">
      <DeniedState reason="insufficient_permission" message="Health Center requires the Health View capability. Contact your org admin." />
    </div>
  );

  return (
    <div className="nx-cc-page">
      <PageHead
        title="Health Center"
        sub={`Real-time health scores and recommendations for ${org.name}`}
        back={returnTo ? { label: returnLabel ? `Back to ${returnLabel}` : 'Back', onClick: () => navigate(returnTo) } : undefined}
        actions={<Btn variant="ghost" icon="RefreshCw" disabled={loading} onClick={load}>Refresh</Btn>}
      />

      {loading ? (
        <div className="nx-empty" style={{ padding: '48px 0' }}><NexusIcon name="Loader" size={20} /><span>Loading health data…</span></div>
      ) : !health ? (
        <div className="nx-empty" style={{ padding: '48px 0' }}><NexusIcon name="AlertCircle" size={20} /><span>Could not load health data.</span></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Overall score + summary stats */}
          <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 20 }}>
            {/* Score card */}
            <div className="nx-cc-chart-card" style={{ padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Overall Health Score</div>
              <div style={{ marginBottom: 14 }}>
                <ScoreRing score={overallScore} color={level.color} />
              </div>
              <Pill tone={level.tone} style={{ fontSize: 13 }}>{level.label}</Pill>
              <p style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 10, lineHeight: 1.5 }}>
                {overallScore >= 70 ? 'Your organization is in good standing.' : 'Some areas need attention to maintain performance.'}
              </p>
            </div>

            {/* Summary tiles */}
            <div className="nx-cc-stat-grid nx-kpi-4">
              <StatCard icon="Users"        label="Active Members"   value={`${health.members?.active ?? 0}`}   sub={`of ${health.members?.total ?? 0} total`}           color="blue"   />
              <StatCard icon="LayoutGrid"   label="Workspaces"       value={`${health.workspaces?.total ?? 0}`} sub={health.workspaces?.max ? `of ${health.workspaces.max} max` : 'no limit'} color="purple" />
              <StatCard icon="MessageSquare" label="Channels Connected" value={`${health.channels?.connected ?? 0}`} sub={`of ${health.channels?.total ?? 0} configured`} color="green"  />
              <StatCard icon="Key"          label="License Usage"    value={`${health.license?.used ?? 0}`}     sub={`of ${health.license?.allotted ?? 0} seats`}         color={((health.license?.used ?? 0) / (health.license?.allotted || 1)) > 0.8 ? 'red' : 'green'} />
            </div>
          </div>

          {/* Score breakdown + recommendations */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            {/* Score breakdown */}
            <div className="nx-cc-chart-card" style={{ padding: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>Score Breakdown</div>
              <ScoreBar label="License Utilization" score={health.scores?.license  ?? 0} icon="Key"            />
              <ScoreBar label="Storage"             score={health.scores?.storage  ?? 0} icon="HardDrive"      />
              <ScoreBar label="Workspaces"          score={health.scores?.workspace ?? 0} icon="LayoutGrid"    />
              <ScoreBar label="Channels"            score={health.scores?.channel  ?? 0} icon="MessageSquare"  />
              <ScoreBar label="Security"            score={health.scores?.security ?? 0} icon="Shield"         />
              <ScoreBar label="AI Platform"         score={health.scores?.ai       ?? 0} icon="Cpu"            />
            </div>

            {/* Recommendations */}
            <div className="nx-cc-chart-card" style={{ padding: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>Recommendations</div>
              {(health.recommendations?.length ?? 0) === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '32px 0', gap: 10 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--nx-success-tint)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <NexusIcon name="CheckCircle" size={22} style={{ color: 'var(--nx-success)' }} />
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>All systems healthy</div>
                  <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', textAlign: 'center' }}>No recommendations at this time. Great work!</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {health.recommendations.map((r, i) => {
                    const rtype = r.type || 'storage';
                    return (
                      <div key={i} style={{ display: 'flex', gap: 12, padding: '12px 0', borderBottom: i < health.recommendations.length - 1 ? '1px solid var(--nx-divider)' : 'none' }}>
                        <div style={{ width: 30, height: 30, borderRadius: 7, background: REC_TONE[rtype] === 'red' ? 'var(--nx-danger-tint)' : REC_TONE[rtype] === 'amber' ? 'var(--nx-warning-tint)' : 'var(--nx-accent-tint)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <NexusIcon name={REC_ICON[rtype] || 'AlertTriangle'} size={14} style={{ color: REC_TONE[rtype] === 'red' ? 'var(--nx-danger)' : REC_TONE[rtype] === 'amber' ? 'var(--nx-warning)' : 'var(--nx-accent)' }} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--nx-text)', lineHeight: 1.5 }}>{r.message}</div>
                          {r.action && <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 2 }}>{r.action}</div>}
                        </div>
                        <Pill tone={REC_TONE[rtype] || 'gray'} style={{ flexShrink: 0, alignSelf: 'flex-start' }}>
                          {rtype.charAt(0).toUpperCase() + rtype.slice(1)}
                        </Pill>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
